import { ApiError, authHeaders, handleUnauthorized, readErrorDetail } from '@/api/client'

async function get(path: string, signal?: AbortSignal): Promise<Response> {
  let response: Response
  try {
    response = await fetch(path, { headers: await authHeaders(), cache: 'no-store', signal })
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    throw new ApiError(0, 'Could not reach the server. Check your connection and try again.')
  }
  if (!response.ok) {
    if (response.status === 401) handleUnauthorized()
    throw new ApiError(response.status, await readErrorDetail(response))
  }
  return response
}

/**
 * The original file (PDF or image) for the in-app viewer. It is fetched as data and
 * rendered by the app — never handed to the browser as a file — and the server only
 * serves it to requests made by the app itself (X-Requested-With).
 */
export async function fetchOriginal(docId: number, signal?: AbortSignal): Promise<{ data: ArrayBuffer; type: string }> {
  const response = await get(`/api/documents/${docId}/view`, signal)
  return { data: await response.arrayBuffer(), type: response.headers.get('content-type') ?? '' }
}

function filenameFrom(disposition: string | null, fallback: string): string {
  if (!disposition) return fallback
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition)
  if (encoded?.[1]) {
    try {
      return decodeURIComponent(encoded[1])
    } catch {
      /* fall through */
    }
  }
  return /filename="?([^";]+)"?/i.exec(disposition)?.[1] ?? fallback
}

/** Save the original file (only offered to people allowed to download). */
export async function downloadOriginal(docId: number, fallbackName: string): Promise<void> {
  const response = await get(`/api/documents/${docId}/download`)
  const blob = await response.blob()
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filenameFrom(response.headers.get('content-disposition'), fallbackName)
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 30_000)
}
